///
/// EarthBrowser
///
/// Created by Matt Giger on 10/27/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The one polite live region for data loading. Screen-reader users hear a
/// single aggregated sentence ("Loading clouds and radar") instead of one
/// announcement per layer, debounced so phase churn inside a sync doesn't
/// chatter. Visual indicators live on the Layers button, rows, legend
/// chips and the timeline; this element is visually hidden.
///

import { LAYERS } from "@/components/controls/layers-panel/layerRegistry"
import { useLayerLoadStates } from "@/hooks/useLayerLoadStates"
import { useEffect, useState } from "react"

const cDebounceMs = 2000

export function LoadStatusAnnouncer() {
	const states = useLayerLoadStates()

	// Visible layers only (hidden ones syncing in the background don't
	// change the picture), named as the Layers panel names them
	const names: string[] = []
	for (const state of states) {
		if (!state.visible || state.done) continue
		const layer = LAYERS.find(l => l.visibility === state.type)
		if (layer && !names.includes(layer.name)) names.push(layer.name)
	}
	const sentence = names.length === 0
		? ""
		: names.length === 1
			? `Loading ${names[0]}`
			: `Loading ${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`

	const [announced, setAnnounced] = useState("")
	useEffect(() => {
		if (sentence === "") {
			// Clear promptly so the region is empty before the next wait
			setAnnounced("")
			return
		}
		const timer = window.setTimeout(() => setAnnounced(sentence), cDebounceMs)
		return () => window.clearTimeout(timer)
	}, [sentence])

	return (
		<div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
			{announced}
		</div>
	)
}
